import React, { useState, useEffect } from 'react';
import { Modal, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  CRUDLayout,
  InputSearch,
  ExportButton,
  CreateButton,
  DeleteButton,
  UpdateButton,
  ActionButton,
  DataStatus,
  CreateModal,
  UpdateModal,
  DeleteModal,
  Input,
  Pagination,
  TextArea,
  Switch,
  THead,
  TBody,
  ThFixed,
  TdFixed,
  Tr,
  Th,
  Td,
} from '../../../../components';
import { LokasiKerjaApi } from '../../../../api';
import { TableNumber } from '../../../../utilities';
import { useIsGuest } from 'hooks';

const LokasiKerja = ({ setNavbarTitle }) => {
  // Title
  const title = 'Lokasi Kerja';
  const guest = useIsGuest();

  // indikator pemanggilan data sedang dimuat di server
  const [isPageLoading, setIsPageLoading] = useState(true);

  // indikator apakah sedang melakukan searching
  const [isSearching, setIsSearching] = useState(false);

  // menampung value dari search form
  const [searchKey, setSearchKey] = useState('');

  // menangani modal form tambah
  const [isCreateForm, setIsCreateForm] = useState(false);
  // menangani modal form ubah
  const [isUpdateform, setIsUpdateform] = useState(false);
  // menangani modal hapus data
  const [isDeleteData, setIsDeleteData] = useState(false);

  // data jenis anggaran
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [dataLength, setDataLength] = useState(10);
  const [dataCount, setDataCount] = useState(0);

  // menampung data yang akan diubah
  const [updateData, setUpdateData] = useState([]);
  // menampung data yang akan dihapus
  const [deleteData, setDeleteData] = useState([]);

  // menampilkan alert
  const [showAlert, setShowAlert] = useState(false);
  // configurasi alert
  const [alertConfig, setAlertConfig] = useState({
    variant: 'primary',
    text: '',
    searchKey: '',
    show: false,
  });

  // request data dari server
  const getData = () => {
    setIsPageLoading(true);
    setIsSearching(false);

    // request data ke server
    LokasiKerjaApi.getPage(page, dataLength, searchKey)
      .then((res) => {
        setData(res.data.data);
        setTotalPage(res.data.total_page);
        setDataCount(res.data.data_count);
      })
      .catch(() =>
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: 'Data gagal dimuat!',
        })
      )
      .finally(() => {
        setIsPageLoading(false);
      });
  };

  // request search data dari server
  const searchData = (e) => {
    // mencegah terjadinya page reload
    e.preventDefault();

    // set loading
    setIsPageLoading(true);

    // nilai dari form search
    const key = searchKey;

    LokasiKerjaApi.search(key)
      .then((res) => {
        setData(res.data.data);
        setTotalPage(res.data.total_page);
        setAlertConfig({
          show: true,
          variant: 'primary',
          searchKey: key,
        });
      })
      .catch(() => {
        setAlertConfig({
          show: true,
          variant: 'danger',
          text: 'Gagal untuk memuat data',
        });
      })
      .finally(() => {
        setPage(1);
        setIsSearching(true);
        setIsPageLoading(false);
        setShowAlert(true);
      });
  };

  // menangani hide/show data
  const changeDataStatus = (status, id) => {
    setIsPageLoading(true);
    setShowAlert(false);

    const value = {
      id_lokasi_kerja: id,
    };

    const onLoadedSuccess = () => {
      setIsSearching(false);
      setAlertConfig({
        show: true,
        variant: 'primary',
        text: 'Ubah status data berhasil',
      });
      setShowAlert(true);
    };

    const onLoadedFailed = () => {
      setIsSearching(false);
      setAlertConfig({
        show: true,
        variant: 'danger',
        text: 'Ubah status data gagal',
      });
      setShowAlert(true);
    };

    status === true
      ? LokasiKerjaApi.show(value)
          .then(() => onLoadedSuccess())
          .catch(() => onLoadedFailed())
          .finally(() => getData())
      : LokasiKerjaApi.hide(value)
          .then(() => onLoadedSuccess())
          .catch(() => onLoadedFailed())
          .finally(() => getData());
  };

  useEffect(() => {
    // set judul di Navbar
    setNavbarTitle(title);

    // jalankan function request data ke server
    getData();

    // menangani pembersihan saat fetching data
    return () => {
      setIsPageLoading(false);
    };
  }, [setNavbarTitle, page, dataLength, searchKey]);

  // modal tambah
  const TambahModal = () => {
    const [alertFormConfig, setAlertFormConfig] = useState({
      data: [],
      show: false,
    });
    // nilai awal form
    const formInitialValues = {
      nama_lokasi_kerja: '',
      keterangan: '',
    };

    // skema validasi form
    const formValidationSchema = Yup.object().shape({
      nama_lokasi_kerja: Yup.string().required('Masukan nama lokasi kerja'),
    });

    // request tambah data ke server
    const formSubmitHandler = (values) => {
      LokasiKerjaApi.create(values)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: 'Tambah data berhasil!',
          });
          // menutup modal
          setIsCreateForm(false);
          // menampilkan alert
          setShowAlert(true);
          // request data baru ke server
          getData();
        })
        .catch((err) => {
          // konfigurasi alert
          setAlertFormConfig({
            data: err.response.data.message,
            show: true,
          });
        })
        .finally(() => {});
    };

    return (
      <CreateModal
        show={isCreateForm}
        onHide={() => setIsCreateForm(false)}
        title={title}
      >
        <Formik
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={formSubmitHandler}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <Alert
                  variant="danger"
                  show={alertFormConfig.show}
                  className="p-1"
                  size="sm"
                >
                  {alertFormConfig?.data?.map((val) => (
                    <ul className="mb-0">
                      <li style={{ fontSize: 13, textTransform: 'capitalize' }}>
                        {val}
                      </li>
                    </ul>
                  ))}
                </Alert>
                <Input
                  label="Nama Lokasi Kerja"
                  placeholder="Masukan nama lokasi kerja"
                  name="nama_lokasi_kerja"
                  value={values.nama_lokasi_kerja}
                  error={
                    errors.nama_lokasi_kerja &&
                    touched.nama_lokasi_kerja &&
                    true
                  }
                  errorText={errors.nama_lokasi_kerja}
                  onChange={handleChange}
                />
                <TextArea
                  label="Keterangan"
                  placeholder="Masukan keterangan"
                  name="keterangan"
                  value={values.keterangan}
                  error={errors.keterangan && touched.keterangan && true}
                  errorText={errors.keterangan}
                  onChange={handleChange}
                />
              </Modal.Body>
              <Modal.Footer>
                <ActionButton
                  type="submit"
                  variant="primary"
                  text="Tambah Data"
                  loading={isSubmitting}
                />
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </CreateModal>
    );
  };

  // modal ubah
  const UbahModal = () => {
    const [alertFormConfig, setAlertFormConfig] = useState({
      data: [],
      show: false,
    });
    // nilai awal form
    const formInitialValues = {
      nama_lokasi_kerja: updateData.nama_lokasi_kerja,
      keterangan: updateData.keterangan,
    };

    // skema validasi form
    const formValidationSchema = Yup.object().shape({
      nama_lokasi_kerja: Yup.string().required('Masukan nama lokasi kerja'),
    });

    // request ubah data ke server
    const formSubmitHandler = (values) => {
      const finalValues = {
        id_lokasi_kerja: updateData.id_lokasi_kerja,
        ...values,
      };

      LokasiKerjaApi.update(finalValues)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: 'Ubah data berhasil!',
          });
          // menutup modal
          setIsUpdateform(false);
          // menampilkan alert
          setShowAlert(true);
          // request data baru ke server
          getData();
        })
        .catch((err) => {
          // konfigurasi alert
          setAlertFormConfig({
            data: err.response.data.message,
            show: true,
          });
        })
        .finally(() => {});
    };

    return (
      <UpdateModal
        show={isUpdateform}
        onHide={() => setIsUpdateform(false)}
        title={title}
      >
        <Formik
          initialValues={formInitialValues}
          validationSchema={formValidationSchema}
          onSubmit={formSubmitHandler}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Modal.Body>
                <Alert
                  variant="danger"
                  show={alertFormConfig.show}
                  className="p-1"
                  size="sm"
                >
                  {alertFormConfig?.data?.map((val) => (
                    <ul className="mb-0">
                      <li style={{ fontSize: 13, textTransform: 'capitalize' }}>
                        {val}
                      </li>
                    </ul>
                  ))}
                </Alert>
                <Input
                  label="Nama Lokasi Kerja"
                  placeholder="Masukan nama lokasi kerja"
                  name="nama_lokasi_kerja"
                  value={values.nama_lokasi_kerja}
                  error={
                    errors.nama_lokasi_kerja &&
                    touched.nama_lokasi_kerja &&
                    true
                  }
                  errorText={errors.nama_lokasi_kerja}
                  onChange={handleChange}
                />
                <TextArea
                  label="Keterangan"
                  placeholder="Masukan keterangan"
                  name="keterangan"
                  value={values.keterangan}
                  error={errors.keterangan && touched.keterangan && true}
                  errorText={errors.keterangan}
                  onChange={handleChange}
                />
              </Modal.Body>
              <Modal.Footer>
                <ActionButton
                  type="submit"
                  variant="success"
                  text="Ubah Data"
                  loading={isSubmitting}
                />
              </Modal.Footer>
            </form>
          )}
        </Formik>
      </UpdateModal>
    );
  };

  // modal hapus
  const HapusModal = () => {
    // set id dari data yang ingin dihapus
    const idData = deleteData.id_lokasi_kerja;
    const values = { id_lokasi_kerja: idData };

    // menangani delete button loading
    const [btnLoading, setBtnLoading] = useState(false);

    // menangani pembersihan saat fetching data
    useEffect(() => {
      return () => {
        setBtnLoading(false);
      };
    }, []);

    // request hapus data ke server
    const deleteDataHandler = () => {
      // set delete button loading
      setBtnLoading(true);

      LokasiKerjaApi.delete(values)
        .then(() => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'primary',
            text: 'Hapus data berhasil!',
          });
        })
        .catch(() => {
          // konfigurasi alert
          setAlertConfig({
            show: true,
            variant: 'danger',
            text: `Hapus data gagal!`,
          });
        })
        .finally(() => {
          // menutup modal
          setIsDeleteData(false);
          // menampilkan alert
          setShowAlert(true);
          // request data baru ke server
          getData();
        });
    };

    return (
      <DeleteModal
        show={isDeleteData}
        onHide={() => setIsDeleteData(false)}
        loading={btnLoading}
        onConfirm={deleteDataHandler}
        title={title}
      >
        <span>Nama Lokasi Kerja : {deleteData.nama_lokasi_kerja}</span>
      </DeleteModal>
    );
  };

  // Tabel
  const Table = () => (
    <>
      <CRUDLayout.Table>
        <THead>
          <Tr>
            <ThFixed>No</ThFixed>
            {guest ? '' : <ThFixed>Aksi</ThFixed>}
            <Th>Nama Lokasi Kerja</Th>
            <Th>Keterangan</Th>
          </Tr>
        </THead>
        <TBody>
          {data.map((val, index) => (
            <Tr key={index}>
              <TdFixed>{TableNumber(page, dataLength, index)}</TdFixed>
              {guest ? (
                ''
              ) : (
                <TdFixed>
                  <div className="d-flex justify-content-center">
                    <UpdateButton
                      onClick={() => {
                        setUpdateData(val);
                        setIsUpdateform(true);
                      }}
                    />
                    <DeleteButton
                      onClick={() => {
                        setDeleteData(val);
                        setIsDeleteData(true);
                      }}
                    />
                    <Switch
                      id={toString(index + 1)}
                      checked={!val.is_hidden}
                      onChange={() =>
                        changeDataStatus(val.is_hidden, val.id_lokasi_kerja)
                      }
                    />
                  </div>
                </TdFixed>
              )}
              <Td>{val.nama_lokasi_kerja}</Td>
              <Td>{val.keterangan}</Td>
            </Tr>
          ))}
        </TBody>
      </CRUDLayout.Table>
      {!isSearching && (
        <Pagination
          dataLength={dataLength}
          dataNumber={page * dataLength - dataLength + 1}
          dataPage={dataCount < dataLength ? dataCount : page * dataLength}
          dataCount={dataCount}
          onDataLengthChange={(e) => {
            setDataLength(e.target.value);
            setPage(1);
          }}
          currentPage={page}
          totalPage={totalPage}
          onPaginationChange={({ selected }) => setPage(selected + 1)}
        />
      )}
    </>
  );

  return (
    <CRUDLayout>
      {/* head */}
      <CRUDLayout.Head>
        {/* search section */}
        <CRUDLayout.HeadSearchSection>
          <div className="d-flex mb-3">
            <InputSearch
              onChange={(e) => {
                const key = e.target.value;
                setSearchKey(key);
                setAlertConfig({
                  show: key ? true : false,
                  variant: 'primary',
                  text: 'Hasil dari pencarian: ' + key,
                });
              }}
            />
          </div>
        </CRUDLayout.HeadSearchSection>

        {/* button section */}
        <CRUDLayout.HeadButtonSection>
          <CreateButton onClick={() => setIsCreateForm(true)} />
        </CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* aLert */}
      <Alert
        show={alertConfig.show}
        variant={alertConfig.variant}
        onClose={() =>
          setAlertConfig({
            ...alertConfig,
            show: false,
          })
        }
      >
        {alertConfig.text}
      </Alert>

      {/* table section */}
      {
        // cek apakah data sedang dimuat (loading)
        isPageLoading === true ? (
          // loading
          <DataStatus loading={true} text="Memuat data..." />
        ) : // Cek apakah ada data
        data.length > 0 ? (
          // Ada data
          <Table />
        ) : (
          // Tidak ada data
          <DataStatus text="Tidak ada data" />
        )
      }

      {/* Modal */}
      <TambahModal />
      <UbahModal />
      <HapusModal />
    </CRUDLayout>
  );
};

export default LokasiKerja;
